import React, { forwardRef } from "react";
import { InputTextarea, InputTextareaProps } from "primereact/inputtextarea";
import { classNames } from "primereact/utils";
import { DashlyFieldShell } from "../shared/DashlyFieldShell";
import { DashlyControlSize, dashlyControlSizeClass } from "../shared/dashlyControlSize";
import { useDashlyFieldValidation } from "../shared/useDashlyFieldValidation";
import { useDebouncedInputChange } from "../shared/useDebouncedInputChange";
import type { FieldValidationRules } from "../../../Util/fieldValidation";
import "../shared/dashlyFormControl.scss";
import "./DashlyTextarea.scss";

export type DashlyTextareaProps = InputTextareaProps & {
    /** Debounce `onChange` (ms). Default 0 (immediate). */
    debounceMs?: number;
    /** Control height scale. Default `md` (2.5rem min-height). */
    controlSize?: DashlyControlSize;
    label?: React.ReactNode;
    labelInfo?: string;
    hint?: React.ReactNode;
    required?: boolean;
    containerClassName?: string;
    /** Entity-aligned validation (maxLength, minLength, required). */
    validation?: FieldValidationRules;
};

const DashlyTextarea = forwardRef<HTMLTextAreaElement, DashlyTextareaProps>(function DashlyTextarea(
    {
        controlSize = "md",
        label,
        labelInfo,
        hint,
        required,
        containerClassName,
        validation,
        className,
        id,
        value,
        maxLength,
        debounceMs = 0,
        onChange,
        onBlur,
        ...rest
    },
    ref,
) {
    const { displayValue, handleChange: debouncedHandleChange, handleBlurFlush } = useDebouncedInputChange({
        value: typeof value === "string" ? value : value == null ? "" : String(value),
        onChange,
        debounceMs,
    });
    const { error, invalid, markTouched, resolvedMaxLength } = useDashlyFieldValidation(
        debounceMs > 0 ? displayValue : value,
        validation,
    );
    const effectiveMaxLength = maxLength ?? resolvedMaxLength;

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        markTouched();
        debouncedHandleChange(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
        markTouched();
        handleBlurFlush(event);
        onBlur?.(event);
    };

    const control = (
        <div
            className={classNames(
                "dashly-textarea-wrap",
                dashlyControlSizeClass(controlSize),
                invalid && "dashly-textarea-wrap--invalid",
            )}
        >
            <InputTextarea
                ref={ref}
                id={id}
                value={displayValue}
                maxLength={effectiveMaxLength}
                aria-invalid={invalid || undefined}
                {...rest}
                onChange={handleChange}
                onBlur={handleBlur}
                className={classNames("dashly-textarea", "w-full", invalid && "p-invalid", className)}
            />
        </div>
    );

    if (label || labelInfo || hint || error) {
        return (
            <DashlyFieldShell
                label={label}
                labelInfo={labelInfo}
                hint={hint}
                error={error}
                htmlFor={id}
                required={required ?? validation?.required}
                className={containerClassName}
            >
                {control}
            </DashlyFieldShell>
        );
    }

    if (error) {
        return (
            <div className={classNames("dashly-field-shell dashly-field-shell--invalid", containerClassName)}>
                {control}
                <span className="dashly-field-shell__error" role="alert">
                    {error}
                </span>
            </div>
        );
    }

    return control;
});

DashlyTextarea.displayName = "DashlyTextarea";

export { DashlyTextarea };
